/**
 * CSV Parser Helper
 * Utilities for parsing CSV files and validating question IDs
 */

import Papa from 'papaparse';

// Error codes for CSV parsing - these are used as resource keys in the component
export const CSV_ERROR_CODES = {
  INVALID_FILE_FORMAT: 'csv_error_invalid_format',
  MISSING_QUESTION_ID_COLUMN: 'csv_error_missing_column',
  NO_VALID_QUESTION_IDS: 'csv_error_no_valid_ids',
  INVALID_QUESTION_IDS: 'csv_error_invalid_ids',
  PROCESSING_ERROR: 'csv_error_processing',
  PARSING_ERROR: 'csv_error_parsing',
} as const;

// Custom error class for CSV parsing errors with error code
export class CSVParseError extends Error {
  code: string;

  constructor(code: string, fallbackMessage: string) {
    super(fallbackMessage);
    this.name = 'CSVParseError';
    this.code = code;
  }
}

/**
 * Validate that a question ID is in the correct format (12 digits)
 * @param id - Question ID to validate
 * @returns true if valid, false otherwise
 */
export function validateQuestionId(id: string): boolean {
  // Question IDs must be exactly 12 digits
  return /^\d{12}$/.test(id.trim());
}

/**
 * Parse CSV file and extract question IDs
 * @param file - CSV file to parse
 * @returns Promise resolving to array of valid question IDs
 * @throws CSVParseError if file is invalid or contains no valid question IDs
 */
export async function parseQuestionIdsFromCSV(
  file: File
): Promise<string[]> {
  return new Promise((resolve, reject) => {
    if (!file.name.endsWith('.csv')) {
      reject(new CSVParseError(
        CSV_ERROR_CODES.INVALID_FILE_FORMAT,
        'Invalid file format. Please upload a CSV file.'
      ));
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const questionIds: string[] = [];
          const invalidIds: string[] = [];

          // Check if CSV has the required column
          if (!results.meta.fields?.includes('question_id')) {
            reject(new CSVParseError(
              CSV_ERROR_CODES.MISSING_QUESTION_ID_COLUMN,
              'CSV file must contain a "question_id" column.'
            ));
            return;
          }

          // Extract and validate question IDs
          results.data.forEach((row: any, index: number) => {
            const id = row.question_id?.toString().trim();
            
            if (id) {
              if (validateQuestionId(id)) {
                // Avoid duplicates
                if (!questionIds.includes(id)) {
                  questionIds.push(id);
                }
              } else {
                invalidIds.push(`Row ${index + 2}: "${id}"`);
              }
            }
          });

          // If there are invalid IDs and no valid ones
          if (invalidIds.length > 0 && questionIds.length === 0) {
            reject(new CSVParseError(
              CSV_ERROR_CODES.INVALID_QUESTION_IDS,
              'Invalid question IDs found (must be 12 digits).'
            ));
            return;
          }

          if (questionIds.length === 0) {
            reject(new CSVParseError(
              CSV_ERROR_CODES.NO_VALID_QUESTION_IDS,
              'No valid question IDs found in the CSV file.'
            ));
            return;
          }

          resolve(questionIds);
        } catch (error) {
          reject(new CSVParseError(
            CSV_ERROR_CODES.PROCESSING_ERROR,
            'Error processing CSV file.'
          ));
        }
      },
      error: () => {
        reject(new CSVParseError(
          CSV_ERROR_CODES.PARSING_ERROR,
          'Error parsing CSV file.'
        ));
      },
    });
  });
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
